import { useQueryClient } from '@tanstack/react-query';
import { useCart } from 'hooks/cart/useCart';
import { useDiscounts } from 'hooks/cart/useDiscounts';
import { useHasCookieConsentResponse } from 'hooks/useCookiebotConsent';
import { Cart as CartConfig, useLocalStorage } from 'hooks/userPreferences';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

const DISCOUNT_PARAM = 'discount';

/**
 * Applies a discount code passed via the `discount` URL query param to the cart.
 *
 * Supports Superfiliate links and paid-ad landing pages that embed a code in the
 * URL. If a session cart exists the code is applied to it; otherwise a cart is
 * created carrying the code (handled in `useDiscounts`). Renders nothing.
 *
 * Uses `next/router` (not `next/navigation`'s `useSearchParams`, which only
 * reliably reflects the query string on server-rendered pages in the pages
 * router - statically generated pages return it empty). We wait for
 * `router.isReady` so `router.query` is populated on every page type, then
 * strip the param from the URL once applied so a refresh or shared link doesn't
 * re-create a cart.
 *
 * We also wait until the shopper has responded to the Cookiebot banner. In
 * `data-blockingmode: 'auto'` Cookiebot purges storage written *before* consent
 * is registered, so creating the cart (which persists the cart id to
 * localStorage) pre-response gets wiped the moment the user accepts *or*
 * declines. Deferring until there is a response means the cart id is written
 * post-consent and survives.
 */
export const QueryParamDiscountHandler = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { addDiscounts } = useDiscounts(queryClient);
  const hasCookieResponse = useHasCookieConsentResponse();
  const { getValue: getCartId } = useLocalStorage(CartConfig);
  // Subscribe to the cart query so this component re-runs its effect once the
  // cart (and its existing discount codes) has loaded - see the guard below.
  const { isFetched: isCartFetched } = useCart();
  const appliedRef = useRef<Set<string>>(new Set());

  const discountParam = router.query[DISCOUNT_PARAM];

  useEffect(() => {
    if (!router.isReady) return;

    // Wait for a cookie decision - see component doc comment.
    if (!hasCookieResponse) return;

    // Spec: the `discount` param may only appear once - ignore arrays.
    if (typeof discountParam !== 'string') return;

    const code = discountParam.trim().toUpperCase();
    if (!code) return;
    if (appliedRef.current.has(code)) return;

    // If a cart already exists, wait until its current codes have loaded into
    // the query cache. `addDiscounts` derives the next code list from that
    // cache; running before it populates would send only this code to Shopify's
    // (replace-semantics) `cartDiscountCodesUpdate` and drop any codes already
    // on the cart. When there is no cart yet, the create path is safe to run.
    if (getCartId()?.id && !isCartFetched) return;

    appliedRef.current.add(code);
    addDiscounts([code]);
    // Strip the param (preserving the rest of the query) synchronously, as soon
    // as we've kicked off the apply - not in the mutation's `onSuccess`. The
    // decline flow triggers a full page reload that would discard a later
    // client-side `router.replace`, stranding the code in the URL (and re-running
    // the mutation on every load). Applying is idempotent (`addDiscounts` unions
    // codes and reuses the cart id), so the param is safe to remove immediately.
    const restQuery = { ...router.query };
    delete restQuery[DISCOUNT_PARAM];
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, {
      shallow: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, hasCookieResponse, discountParam, isCartFetched]);

  return null;
};
